/** @module csi/heading/Specs */
define([
	"dojo/_base/declare",
	"dojo/store/Memory",
	"lodash",
	"csi/core/Key",
	"csi/xml"
], function(declare, Memory, {isEqual, pick, uniqueId}, Key, xml) {

	function getMapKey(value, isMatrixGroup) {
		return isMatrixGroup ? `{${value}}` : value;
	}

	return declare(null, {
		// Boolean: indicates if there are sub-cells
		bSubCells: false,

		// Spec[]: cell specs
		cells: null,

		// Object: map of specs by id
		_map: null,

		/** @property {Record<C8AttributeKey, Spec>} _mapByKey - map of specs by typed key */
		_mapByKey: null,

		/** @property {Record<C8AttributeKey, Spec>} _mapByUntypedKey - map of specs by untyped key */
		_mapByUntypedKey: null,

		/**
		 * @param {Spec[]} [specs] 
		 */
		constructor(specs) {
			this.cells = [];
			this._map = {};
			this._mapByKey = {};
			this._mapByUntypedKey = {};

			if (specs) {
				for (const spec of specs) {
					this.add(spec);
				}
			}
		},

		add: function(/*Spec*/spec) {
			// summary: add spec
			this._init(spec);
			this.cells.push(spec);
			return spec;
		},

		/**
		 * Find spec by given key parameters;
		 * if looking for a spec within a matrix, then "matrix" parameter must be specified
		 * @param {IKey} params
		 * @return {Spec | undefined}
		 */
		find(params) {
			const {matrix} = params;
			const targetKeys = Object.keys(params);

			const findSpec = (specs) => {
				let targetSpec;

				specs.some((spec) => {
					const {isGroup, isMatrixGroup, key, parentSpec, subs} = spec;

					if (parentSpec || !matrix) {
						const targetKey = pick(key, targetKeys);

						if (isEqual(targetKey, params)) {
							targetSpec = spec;
						}
					}

					if (!targetSpec && isGroup) {
						// traverse subs if spec is an attribute group (and not looking for a matrix), or is
						// a matrix group and looking for a key within a matrix
						if (!!matrix === isMatrixGroup) {
							targetSpec = findSpec(subs);
						}
					}

					return !!targetSpec;
				});

				return targetSpec;
			};

			return findSpec(this.cells);
		},

		get: function(/*String*/id) {
			// summary: return spec by unique id
			return this._map[id];
		},

		/**
		 * Return spec for given key
		 * @param {C8AttributeKey | Key} key 
		 * @param {boolean} [typedOnly] - if true, then only check typed string
		 * @param {boolean} [onlyIfMatrixGroup] - if true, then return the spec only if it is a matrix group
		 * @param {boolean} [ignoreMatrixGroup] - if true, then do not return the spec if it is a matrix; such keys can conflict with those that represent (non-matrix) lists
		 * @return {Spec | undefined}
		 */
		getByKey(key, typedOnly, onlyIfMatrixGroup, ignoreMatrixGroup) {
			const {_mapByKey, _mapByUntypedKey} = this;
			const {typedString, untypedString} = Key.toKey(key);
			const typedMapKey = getMapKey(typedString, onlyIfMatrixGroup);
			const untypedMapKey = getMapKey(untypedString, onlyIfMatrixGroup);
			let spec = _mapByKey[typedMapKey] || (typedOnly ? undefined : _mapByUntypedKey[untypedMapKey]);

			if (!spec && !onlyIfMatrixGroup) {
				spec = this.getByKey(key, typedOnly, true);
			}

			if (ignoreMatrixGroup && spec?.isMatrixGroup) {
				return;
			}

			return spec;
		},

		getByQueryKey: function(/*String*/key, /*Boolean*/omitSubs) {
			// summary: iterate through data to find spec that matches query key
			var spec;

			this.traverse(!omitSubs, function(c, sc) {
				if (!spec) {
					c = sc || c;

					if (c.queryKey == key) {
						spec = c;
					}
				}
			});

			return spec;
		},

		/**
		 * Return spec by index; if includeSubs is true, then also traverse sub-specs
		 * @param {number} index
		 * @param {boolean} [includeSubs]
		 * @return {Spec}
		 */
		getByIndex: function(index, includeSubs) {
			if (includeSubs) {
				var spec;

				this.traverse(true, function(c, sc, i, n) {
					if (index === n) {
						spec = sc || c;
					}
				});

				return spec;
			}

			return this.cells[index];
		},

		/**
		 * Return array of indices where spec can be copied if it is configured to copy to other columns within a matrix
		 * @param {Spec} spec
		 * @return {number[]|null}
		 */
		getCopyIndices: function(spec) {
			if (!spec.allowCopyToMatrixColumns) {
				return null;
			}

			var h = spec.heading;
			var indices = [];

			this.traverse(true, function(c, sc, i, n) {
				var s = sc || c;
				if (s.heading === h) {
					indices.push(n);
				}
			});

			return indices;
		},

		getQueryStore: function(/*Boolean*/bSubs, /*Spec[]?*/extras, /*Function?*/filterFunc) {
			// summary: return store of specs that have querySpec
			const specs = extras?.filter((spec) => !this.getByQueryKey(spec.queryKey)) ?? [];

			this.traverse(bSubs, (c, sc) => {
				sc = sc || c;

				if (sc.querySpec && (!filterFunc || filterFunc(sc.queryKey, c, sc))) {
					specs.push(sc);
				}
			});

			return new Memory({
				data: specs.map((spec) => ({
					id: spec.queryKey,
					name: spec.name,
					spec,
				})),
			});
		},

		getSpecCount: function() {
			// summary: return number of columns, including sub-headings; sub-headings by default count, whereas some
			// top-level specs may not (i.e. actions column)
			var n = 0;

			this.traverse(true, function(c, sc) {
				if (sc || c.heading) {
					++n;
				}
			});

			return n;
		},

		/**
		 * Return all specs of given vector attribute with any index
		 * @param {string} attributeId 
		 * @param {string} attributeStandardPath 
		 * @return {Spec[]}
		 */
		getVectorSpecs(attributeId, attributeStandardPath) {
			return this.cells.filter((spec) => {
				const {key, querySpec} = spec;

				// NOTE: use key.path as it is a standard path; querySpec.path could be an extended path
				return (querySpec?.index != null) &&
					(querySpec.id === attributeId) &&
					xml.pathsAreSame(attributeStandardPath, key.path);
			});
		},

		isEmpty: function() {
			// summary: return if there are any cells
			return !this.cells.length;
		},

		/**
		 * Remove specs by some condition
		 * @param {boolean} bSubs - indicates if sub-specs should be traversed
		 * @param {(spec: Spec) => boolean} condition - return true from function to remove the spec
		 */
		remove(bSubs, condition) {
			this.cells = this.cells.filter((spec) => {
				if (condition(spec)) {
					return false;
				}

				if (bSubs && spec.subs?.length) {
					spec.subs = spec.subs.filter((spec) => !condition(spec));

					if (!spec.subs.length) {
						return false;
					}
				}

				return true;
			});
		},

		traverse: function(/*Boolean*/bSubs, /*Function*/f, /*Object?*/scope) {
			// summary: traverse cells to apply function in given scope - return number of cells traversed
			var cells = this.cells;
			var i = -1;
			var n = 0;
			var c;
			var subs;

			if (bSubs &= this.bSubCells) {
				var j;
				var sc;

				n = -1;

				while (c = cells[++i]) {
					if (subs = c.subs) {
						j = -1;

						while (sc = subs[++j]) {
							f.call(scope, c, sc, i, ++n);
						}
					} else {
						f.call(scope, c, null, i, ++n);
					}
				}

				return ++n;
			}

			while (c = cells[++i]) {
				f.call(scope, c, null, i, n);

				if (subs = c.subs) {
					n += subs.length;
				} else {
					++n;
				}
			}

			return i;
		},

		_init: function(/*Spec*/spec) {
			// summary: assign view id to specs and sub-specs
			const {_map, _mapByKey, _mapByUntypedKey} = this;
			const {isMatrixGroup, key, subs} = spec;

			spec.sid ||= uniqueId("SID");
			_map[spec.sid] = spec;

			if (key) {
				const {typedString, untypedString} = key;
				if (typedString) {
					_mapByKey[getMapKey(typedString, isMatrixGroup)] = spec;
				}
				if (untypedString) {
					_mapByUntypedKey[getMapKey(untypedString, isMatrixGroup)] = spec;
				}
			}

			if (subs) {
				this.bSubCells = true;
				subs.forEach((sub) => this._init(sub));
			}
		}
	});
});